import type { CSSProperties } from 'react';
import type { WrappedResponse } from '../../api/types';
import { LINES, lineNamedBy } from '../lines';
import { fixed, int, share } from './format';

/**
 * The pictures under each stop's number. All of them draw in with CSS only
 * (no chart library): each takes the stop's `delay` and animates from there,
 * so a chart comes in on the same beat as the words around it.
 */

const vars = (v: Record<string, string | number>): CSSProperties => v as CSSProperties;
const clamp = (n: number, lo = 0, hi = 100) => Math.min(hi, Math.max(lo, n));

/**
 * Where the rider stands among everyone, drawn as a stretch of line: the
 * typical rider is the station in the middle, and "YOU" is a train that runs
 * out along the track to its percentile.
 */
export function Gauge({
  value,
  left,
  right,
  mid = 'TYPICAL',
  delay,
}: {
  /** 0–100, how far along the axis from `left` to `right`. */
  value: number;
  left: string;
  right: string;
  mid?: string;
  delay: number;
}) {
  return (
    <div className="gauge st" style={vars({ '--d': `${delay}ms`, '--p': `${clamp(value, 2, 98)}%` })}>
      <div className="g-track">
        <i className="g-fill" />
        <span className="g-mid">
          <i />
          <small>{mid}</small>
        </span>
        <span className="g-you">YOU</span>
      </div>
      <div className="g-ends">
        <span>◀ {left}</span>
        <span>{right} ▶</span>
      </div>
    </div>
  );
}

/**
 * Two scores out of ten on one ruler, the gap between them shaded. The ruler
 * shows only the stretch around them (at least three points wide), so 7.9
 * against 8.1 is a visible gap rather than two pins on top of each other.
 */
export function ScoreScale({
  you,
  them,
  themLabel = 'EVERYONE',
  delay,
}: {
  you: number;
  them: number;
  themLabel?: string;
  delay: number;
}) {
  let from = Math.max(0, Math.floor(Math.min(you, them)) - 1);
  let to = Math.min(10, Math.ceil(Math.max(you, them)) + 1);
  if (to - from < 3) {
    if (from === 0) to = 3;
    else from = Math.max(0, to - 3);
  }
  const pos = (score: number) => clamp(((score - from) / (to - from)) * 100);
  const lo = pos(Math.min(you, them));
  const width = pos(Math.max(you, them)) - lo;
  return (
    <div className="scale st" style={vars({ '--d': `${delay}ms` })}>
      <span className="s-pin you" style={vars({ '--p': `${pos(you)}%` })}>
        <b>YOU {fixed(you)}</b>
      </span>
      <div className="s-rule">
        <i
          className={`s-gap ${you < them ? 'low' : 'high'}`}
          style={vars({ left: `${lo}%`, width: `${width}%` })}
        />
        <span className="s-end l">{from}</span>
        <span className="s-end r">{to}</span>
        {Array.from({ length: to - from - 1 }, (_, n) => (
          <span key={n} className="s-tick" style={vars({ left: `${pos(from + n + 1)}%` })} />
        ))}
      </div>
      <span className="s-pin them" style={vars({ '--p': `${pos(them)}%` })}>
        <b>
          {themLabel} {fixed(them)}
        </b>
      </span>
    </div>
  );
}

/**
 * The genres on the rider's list, each bar in the colour of the line it rides,
 * so the order here is visibly the order the network will run in.
 */
export function GenreBars({
  rows,
  typical,
  delay,
}: {
  rows: { genre: string; share: number }[];
  typical: number;
  delay: number;
}) {
  const top = Math.max(typical, ...rows.map((r) => r.share), 0.01);
  return (
    <ul className="gbars st" style={vars({ '--d': `${delay}ms`, '--t': `${(typical / top) * 100}%` })}>
      {rows.map((r, i) => {
        const slug = lineNamedBy(r.genre);
        const line = slug ? LINES.find((l) => l.slug === slug) : undefined;
        return (
          <li
            key={r.genre}
            style={vars({
              '--c': line?.color ?? '#7a8396',
              '--w': `${(r.share / top) * 100}%`,
              '--i': i,
            })}
          >
            <span className="gb-name">{r.genre}</span>
            <span className="gb-bar">
              <i />
              {line && <em>{line.kanji}</em>}
              {i === 0 && <span className="gb-typ" title="The typical rider's top genre" />}
            </span>
            <span className="gb-pct">{share(r.share)}</span>
          </li>
        );
      })}
      <li className="gb-key" aria-hidden="true">
        <span />
        <span>
          <i /> TYPICAL RIDER'S TOP GENRE
        </span>
        <span />
      </li>
    </ul>
  );
}

/** Every show a rider starts, as a row of tickets; the dropped ones are torn. */
export function TicketStrip({ rate, dropped, delay }: { rate: number; dropped: number; delay: number }) {
  const cells = 40;
  const torn = dropped > 0 ? Math.max(1, Math.round(rate * cells)) : 0;
  // Torn tickets spread through the strip rather than bunched at one end.
  const tornAt = new Set(Array.from({ length: torn }, (_, k) => Math.floor(((k + 0.5) * cells) / torn)));
  return (
    <div className="tix st" style={vars({ '--d': `${delay}ms` })}>
      <div className="tix-row">
        {Array.from({ length: cells }, (_, i) => (
          <i key={i} className={tornAt.has(i) ? 'torn' : undefined} style={vars({ '--i': i })} />
        ))}
      </div>
      <small>
        {torn === 0
          ? 'OUT OF EVERY 40 SHOWS YOU START, YOU FINISH ALL 40'
          : `OUT OF EVERY 40 SHOWS YOU START, ABOUT ${torn} GET LEFT BEHIND`}
      </small>
    </div>
  );
}

/** Plain bars for two counts, to scale, so a tiny one looks tiny. */
export function Bars({
  rows,
  delay,
}: {
  rows: { label: string; value: number; unit: string; accent?: boolean; text?: string }[];
  delay: number;
}) {
  const top = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="bars2 st" style={vars({ '--d': `${delay}ms` })}>
      {rows.map((r, i) => (
        <li
          key={r.label}
          className={r.accent ? 'accent' : undefined}
          style={vars({ '--w': `${Math.max(1.5, (r.value / top) * 100)}%`, '--i': i })}
        >
          <small>{r.label}</small>
          <span className="b2-bar">
            <i />
          </span>
          <b>{r.text ?? `${int(r.value)} ${r.unit}`}</b>
        </li>
      ))}
    </ul>
  );
}

/** Columns rising from a baseline: how many of something fell in each bucket. */
export function Columns({
  values,
  labels,
  hot,
  delay,
}: {
  values: number[];
  labels: string[];
  /** Which columns to light up (the 9s and 10s, the rider's busiest decade). */
  hot: (i: number) => boolean;
  delay: number;
}) {
  const top = Math.max(...values, 1);
  return (
    <div className="tcols st" style={vars({ '--d': `${delay}ms` })}>
      {values.map((v, i) => (
        <div key={labels[i]} className={`col${hot(i) ? ' hot' : ''}`} style={vars({ '--i': i })}>
          <small>{v > 0 ? int(v) : ''}</small>
          <span>
            <i style={vars({ '--h': `${v > 0 ? Math.max(3, (v / top) * 100) : 0}%` })} />
          </span>
          <b>{labels[i]}</b>
        </div>
      ))}
    </div>
  );
}

const FORMAT_LABEL: Record<string, string> = {
  TV: 'TV',
  MOVIE: 'MOVIES',
  ONA: 'WEB',
  OVA: 'OVA & SPECIALS',
  SHORT: 'SHORTS',
};
const FORMAT_COLOR: Record<string, string> = {
  TV: '#8FA3C4',
  MOVIE: '#FFB01F',
  ONA: '#34E5FF',
  OVA: '#B266FF',
  SHORT: '#7CE0A0',
};

/** The rider's list and a typical list, each one bar split by format. */
export function FormatMix({
  mix,
  delay,
}: {
  mix: { format: string; share: number; typical_share: number }[];
  delay: number;
}) {
  const row = (who: string, pick: (m: (typeof mix)[number]) => number, i: number) => (
    <li style={vars({ '--i': i })}>
      <small>{who}</small>
      <span className="fm-bar">
        {mix.map((m) =>
          pick(m) > 0 ? (
            <i
              key={m.format}
              style={vars({ '--w': `${pick(m) * 100}%`, '--c': FORMAT_COLOR[m.format] ?? '#888' })}
              title={`${FORMAT_LABEL[m.format]} ${share(pick(m))}`}
            />
          ) : null,
        )}
      </span>
    </li>
  );
  return (
    <div className="fmix st" style={vars({ '--d': `${delay}ms` })}>
      <ul>
        {row('YOU', (m) => m.share, 0)}
        {row('TYPICAL RIDER', (m) => m.typical_share, 1)}
      </ul>
      <p className="fm-key">
        {mix.map((m) => (
          <span key={m.format} style={vars({ '--c': FORMAT_COLOR[m.format] ?? '#888' })}>
            <i /> {FORMAT_LABEL[m.format]} {share(m.share)}
          </span>
        ))}
      </p>
    </div>
  );
}

/**
 * A show in progress as a stretch of line: one station per episode (or a
 * plain track for long shows), the train at the episode the rider is on, the
 * finale the terminus.
 */
export function EpisodeRail({ progress, episodes, delay }: { progress: number; episodes: number; delay: number }) {
  const dots = episodes <= 26;
  const p = Math.min(100, (progress / Math.max(episodes, 1)) * 100);
  return (
    <div className="erail st" style={vars({ '--d': `${delay}ms`, '--p': `${p}%` })}>
      <div className="er-track">
        <i className="er-fill" />
        {dots &&
          Array.from({ length: episodes }, (_, n) => (
            <span
              key={n}
              className={`er-stop${n + 1 <= progress ? ' done' : ''}${n + 1 === episodes ? ' end' : ''}`}
              style={vars({ left: `${((n + 1) / episodes) * 100}%`, '--i': n })}
            />
          ))}
        <span className="er-train">EP {progress}</span>
      </div>
      <div className="g-ends">
        <span>EP 1</span>
        <span>最終話 FINALE ・ EP {episodes}</span>
      </div>
    </div>
  );
}

/** The themes a list keeps coming back to, stamped like ticket punches. */
export function Stamps({
  tags,
  delay,
}: {
  tags: { tag: string; lift: number; example: string | null }[];
  delay: number;
}) {
  return (
    <ul className="stamps">
      {tags.map((t, i) => (
        <li key={t.tag} className="st" style={vars({ '--d': `${delay + i * 450}ms` })}>
          <b>{t.tag}</b>
          <em>{t.lift.toFixed(1)}×</em>
          {t.example && <small>like {t.example}</small>}
        </li>
      ))}
    </ul>
  );
}

/** The drawn map's shape: wider than tall, so it fits under the stop's words. */
const MAP_W = 480;
const MAP_H = 260;
/** Cells this close to the rider (in SDs) light up as their neighbourhood. */
const NEAR = 0.6;

/**
 * Everyone's taste as a dot map, the rider pinned on it. Each dot is a patch
 * of the map, sized by how many people's lists land there; the four edges are
 * named after the genres that pull a list that way. The patches nearest the
 * rider light up amber as they come in, and the YOU roundel drops in last.
 */
export function TasteMap({
  map,
  delay,
}: {
  map: NonNullable<WrappedResponse['taste_map']>;
  delay: number;
}) {
  const [x0, x1, y0, y1] = map.bounds;
  const nx = map.grid.length;
  const ny0 = map.grid[0]?.length ?? 0;
  // Merge rows so the cells come out roughly square at the map's shape.
  const fy = Math.max(1, Math.round(ny0 / (nx * (MAP_H / MAP_W))));
  const ny = Math.ceil(ny0 / fy);
  const cells: { i: number; j: number; n: number }[] = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      let n = 0;
      for (let k = j * fy; k < Math.min(ny0, (j + 1) * fy); k++) n += map.grid[i]![k]!;
      cells.push({ i, j, n });
    }
  }
  const top = Math.max(1, ...cells.map((c) => c.n));
  const cw = MAP_W / nx;
  const ch = MAP_H / ny;
  const px = (x: number) => ((x - x0) / (x1 - x0)) * MAP_W;
  const py = (y: number) => MAP_H - ((y - y0) / (y1 - y0)) * MAP_H;
  const you = { x: clamp(px(map.x), 10, MAP_W - 10), y: clamp(py(map.y), 10, MAP_H - 10) };
  const sx = (x1 - x0) / nx;
  const sy = ((y1 - y0) / ny0) * fy;
  const label = (end: keyof typeof map.ends) => map.ends[end].join(' · ').toUpperCase();

  return (
    <div className="tmap st" style={vars({ '--d': `${delay}ms` })}>
      <span className="tm-end up">▲ {label('up')}</span>
      <div className="tm-mid">
        <span className="tm-end left">◀ {label('left')}</span>
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} role="img" aria-label="Map of everyone's taste, with you on it">
          <line className="tm-axis" x1={px(0)} y1="0" x2={px(0)} y2={MAP_H} />
          <line className="tm-axis" x1="0" y1={py(0)} x2={MAP_W} y2={py(0)} />
          {cells.map(({ i, j, n }) => {
            if (!n) return null;
            const cx = (i + 0.5) * cw;
            const cy = MAP_H - (j + 0.5) * ch;
            const near = Math.hypot(x0 + (i + 0.5) * sx - map.x, y0 + (j + 0.5) * sy - map.y) <= NEAR;
            const r = Math.max(0.9, (Math.min(cw, ch) / 2 - 0.6) * Math.sqrt(n / top));
            return (
              <circle
                key={`${i}.${j}`}
                className={near ? 'near' : undefined}
                cx={cx.toFixed(1)}
                cy={cy.toFixed(1)}
                r={r.toFixed(2)}
                style={vars({ '--i': i, '--o': (0.3 + 0.7 * Math.sqrt(n / top)).toFixed(2) })}
              />
            );
          })}
          <g className="tm-mid-mark" transform={`translate(${px(0)} ${py(0)})`}>
            <circle r="5" />
          </g>
          <g className="tm-you" transform={`translate(${you.x.toFixed(1)} ${you.y.toFixed(1)})`}>
            <circle className="tm-pulse" r="16" />
            <g className="tm-pin">
              <circle r="11" />
              <text y="3.6">YOU</text>
            </g>
          </g>
        </svg>
        <span className="tm-end right">{label('right')} ▶</span>
      </div>
      <span className="tm-end down">▼ {label('down')}</span>
    </div>
  );
}
